/**
 * Brand identity — single source of truth.
 *
 * 想要快速换品牌名（如把 "Horsir / 弼马温" 改成别的），只需修改本文件中的常量，
 * 任务面板顶栏、欢迎卡片、系统提示词、导出文件名、HTML <title> 等会自动跟随。
 *
 * 注意：
 * - `BRAND_SLUG` 用于生成导出文件名（对用户可见），可以随品牌变。
 * - `BRAND_STORAGE_PREFIX` 是 localStorage 的命名空间前缀，**改了会导致老用户丢失配置/历史**，
 *   因此默认与 BRAND_SLUG 解耦，独立维护；除非确实要做不兼容迁移，否则不要动。
 * - 静态资源里的硬编码（manifest.xml、package.json、public/*.html、README.md、server/*.mjs）
 *   不在运行时取值，需要手工同步，已在每个常量上方注明同步点。
 */

/** 主品牌名（英文 / 默认显示名）。
 *  同步点：manifest.xml、package.json、public/help.html、public/policy.html、README.md */
export const BRAND_NAME = "Horsir";

/** 中文副名（与 BRAND_NAME 拼接展示时使用，可空）。
 *  同步点：src/core/i18n.ts 的 SYS_PROMPTS（如改了这里，同步把 prompt 文案改一下）。 */
export const BRAND_CN_NAME = "弼马温";

/** 品牌副标语 / 来源解释（系统提示词中作为括号注释出现，可空）。 */
export const BRAND_TAGLINE = "Horse sir";

/** URL / 文件名用的 slug（小写、无空格）。
 *  同步点：package.json 的 "name"、server/server.mjs 中的 "horsir-agent" 标识。 */
export const BRAND_SLUG = "horsir";

/** localStorage 命名空间前缀。**改了会让老用户丢配置**，默认固定不随品牌变。 */
export const BRAND_STORAGE_PREFIX = "horsir";

/** 一句话产品描述（用于 manifest Description、欢迎页副标题等）。
 *  同步点：manifest.xml 的 <Description>。 */
export const BRAND_DESC =
  "在 Word / Excel / PowerPoint 中改写、生成内容并分析数据，支持任意 OpenAI 兼容接口。";

/** 用于系统提示词中的「智能体自称」，形如 "Horsir (Horse sir 中文名 弼马温 🐎)"。
 *  若不想要副标语，可直接返回 BRAND_NAME。 */
export function brandAgentName(): string {
  const parts: string[] = [BRAND_NAME];
  const extras: string[] = [];
  if (BRAND_TAGLINE) extras.push(BRAND_TAGLINE);
  if (BRAND_CN_NAME) extras.push(`中文名 ${BRAND_CN_NAME}`);
  if (extras.length) parts.push(`(${extras.join(" · ")} 🐎)`);
  return parts.join(" ");
}

/** 顶栏 / 文档标题里展示的完整名称（如 "Horsir" 或 "Horsir（弼马温）"）。 */
export function brandDisplay(): string {
  return BRAND_CN_NAME ? `${BRAND_NAME}（${BRAND_CN_NAME}）` : BRAND_NAME;
}

/** 欢迎卡片中的自我介绍：zh → "你好，我是 Horsir"，en → "Hi, I'm Horsir"。 */
export function brandGreeting(lang: "zh" | "en"): string {
  return lang === "zh" ? `你好，我是 ${BRAND_NAME}` : `Hi, I'm ${BRAND_NAME}`;
}

/** 聊天记录导出时给助手消息加的标签，如 "## Horsir"。 */
export function brandAssistantLabel(): string {
  return `## ${BRAND_NAME}`;
}
